import { getDefaultConfig } from '@rainbow-me/rainbowkit';
import { http } from 'wagmi';
import {
  arbitrum,
  arbitrumNova,
  astarZkEVM,
  avalanche,
  base,
  blast,
  bob,
  bsc,
  immutableZkEvm,
  kroma,
  linea,
  mainnet,
  manta,
  mantle,
  mode,
  okc,
  opBNB,
  optimism,
  pgn,
  polygon,
  polygonZkEvm,
  ronin,
  scroll,
  taiko,
  xLayer,
  zetachain,
  zkSync,
  zora,
} from 'wagmi/chains';

// Reliable public RPC endpoints that support CORS.
// RainbowKit's defaults (eth.merkle.io etc.) block cross-origin requests,
// so we must provide explicit transports for every chain.
// Override any chain via VITE_<CHAIN>_RPC_URL environment variables.
const rpc = (envKey: string, fallbackUrl: string) =>
  http(import.meta.env[envKey] || fallbackUrl, { retryCount: 3, retryDelay: 1000 });

export const config = getDefaultConfig({
  appName: 'Nostra',
  projectId: import.meta.env.VITE_APP_WALLET_CONNECT_ID!,
  chains: [
    mainnet,
    optimism,
    arbitrum,
    arbitrumNova,
    astarZkEVM,
    avalanche,
    base,
    blast,
    bob,
    bsc,
    immutableZkEvm,
    kroma,
    linea,
    manta,
    mantle,
    mode,
    okc,
    opBNB,
    pgn,
    polygon,
    polygonZkEvm,
    ronin,
    scroll,
    taiko,
    xLayer,
    zetachain,
    zkSync,
    zora,
  ],
  transports: {
    [mainnet.id]: rpc('VITE_ETHEREUM_RPC_URL', 'https://eth.llamarpc.com'),
    [optimism.id]: rpc('VITE_OPTIMISM_RPC_URL', 'https://optimism.llamarpc.com'),
    [arbitrum.id]: rpc('VITE_ARBITRUM_RPC_URL', 'https://arbitrum.llamarpc.com'),
    [arbitrumNova.id]: rpc('VITE_ARBITRUM_NOVA_RPC_URL', 'https://arbitrum-nova.publicnode.com'),
    [astarZkEVM.id]: rpc('VITE_ASTAR_ZKEVM_RPC_URL', 'https://rpc.startale.com/astar-zkevm'),
    [avalanche.id]: rpc('VITE_AVALANCHE_RPC_URL', 'https://avalanche-c-chain-rpc.publicnode.com'),
    [base.id]: rpc('VITE_BASE_RPC_URL', 'https://base.llamarpc.com'),
    [blast.id]: rpc('VITE_BLAST_RPC_URL', 'https://rpc.blast.io'),
    [bob.id]: rpc('VITE_BOB_RPC_URL', 'https://rpc.gobob.xyz'),
    [bsc.id]: rpc('VITE_BSC_RPC_URL', 'https://bsc-rpc.publicnode.com'),
    [immutableZkEvm.id]: rpc('VITE_IMMUTABLE_ZKEVM_RPC_URL', 'https://rpc.immutable.com'),
    [kroma.id]: rpc('VITE_KROMA_RPC_URL', 'https://api.kroma.network'),
    [linea.id]: rpc('VITE_LINEA_RPC_URL', 'https://rpc.linea.build'),
    [manta.id]: rpc('VITE_MANTA_RPC_URL', 'https://pacific-rpc.manta.network/http'),
    [mantle.id]: rpc('VITE_MANTLE_RPC_URL', 'https://rpc.mantle.xyz'),
    [mode.id]: rpc('VITE_MODE_RPC_URL', 'https://mainnet.mode.network'),
    [okc.id]: rpc('VITE_OKC_RPC_URL', 'https://exchainrpc.okex.org'),
    [opBNB.id]: rpc('VITE_OPBNB_RPC_URL', 'https://opbnb-mainnet-rpc.bnbchain.org'),
    [pgn.id]: rpc('VITE_PGN_RPC_URL', 'https://rpc.publicgoods.network'),
    [polygon.id]: rpc('VITE_POLYGON_RPC_URL', 'https://polygon.llamarpc.com'),
    [polygonZkEvm.id]: rpc('VITE_POLYGON_ZKEVM_RPC_URL', 'https://zkevm-rpc.com'),
    [ronin.id]: rpc('VITE_RONIN_RPC_URL', 'https://api.roninchain.com/rpc'),
    [scroll.id]: rpc('VITE_SCROLL_RPC_URL', 'https://rpc.scroll.io'),
    [taiko.id]: rpc('VITE_TAIKO_RPC_URL', 'https://rpc.mainnet.taiko.xyz'),
    [xLayer.id]: rpc('VITE_XLAYER_RPC_URL', 'https://rpc.xlayer.tech'),
    [zetachain.id]: rpc('VITE_ZETACHAIN_RPC_URL', 'https://zetachain-evm.blockpi.network/v1/rpc/public'),
    [zkSync.id]: rpc('VITE_ZKSYNC_RPC_URL', 'https://mainnet.era.zksync.io'),
    [zora.id]: rpc('VITE_ZORA_RPC_URL', 'https://rpc.zora.energy'),
  },
  ssr: false,
});

/** Union of all chain IDs configured in the wagmi config. */
export type ConfiguredChainId = (typeof config)['chains'][number]['id'];
